// The chart frame (R2742, ui-map-interface.md §17.3.05): the latitude and
// the longitude of a cell, in degrees and minutes, as a sea chart writes a
// position. One cell is one minute of arc, so the graticule's line every
// `every_cells` cells is a line every that many minutes. Row 0 stands at
// `lat_origin_min` and each row lies one minute further south; column 0
// stands at `lon_origin_min` and each column lies one minute further east.
//
// The graticule, the neat line and the border band write their edge
// figures through `latFig` and `lonFig`, and every peek and every place
// line writes `cellPos`, so a reader finds the position of a card on the
// edge of the chart.

import { CHART } from '../gen/presentation';
import { chart as S } from '../gen/strings';
import { pad2 } from '../locale/stamp';
import { COLS, cellAt } from '../world';

const FRAME = CHART.pieces.graticule.fig;

/** Minutes of arc in one degree, and the bounds of the two axes. */
const MIN_PER_DEG = 60;
const POLE_MIN = 90 * MIN_PER_DEG;
const HALF_TURN_MIN = 180 * MIN_PER_DEG;

/** The latitude of a row, in signed minutes, north positive. */
export function rowLat(row: number): number {
  return FRAME.lat_origin_min - row;
}

/** The longitude of a column, in signed minutes, east positive. */
export function colLon(col: number): number {
  return FRAME.lon_origin_min + col;
}

/**
 * A latitude as a chart writes it: `14°05′N`. It takes signed minutes,
 * rounds to the whole minute and stops at a pole. The equator reads N.
 */
export function latFig(min: number): string {
  const m = Math.max(-POLE_MIN, Math.min(POLE_MIN, Math.round(min)));
  const a = Math.abs(m);
  return S.pos_lat({
    d: pad2(Math.floor(a / MIN_PER_DEG)), m: pad2(a % MIN_PER_DEG),
    h: m < 0 ? S.hemi_s : S.hemi_n,
  });
}

/**
 * A longitude as a chart writes it: `031°55′W`, three figures of degrees.
 * It takes signed minutes, rounds to the whole minute and wraps past the
 * antimeridian, which reads W. The prime meridian reads E.
 */
export function lonFig(min: number): string {
  const r = Math.round(min);
  const m = ((((r + HALF_TURN_MIN) % (2 * HALF_TURN_MIN)) + 2 * HALF_TURN_MIN) % (2 * HALF_TURN_MIN)) - HALF_TURN_MIN;
  const a = Math.abs(m);
  return S.pos_lon({
    d: String(Math.floor(a / MIN_PER_DEG)).padStart(3, '0'), m: pad2(a % MIN_PER_DEG),
    h: m < 0 ? S.hemi_w : S.hemi_e,
  });
}

/** The position of a column and a row: `14°05′N 031°55′W`. */
export function colRowPos(col: number, row: number): string {
  return S.pos({ lat: latFig(rowLat(row)), lon: lonFig(colLon(col)) });
}

/** The position of cell `i` of the live grid, or '' for no cell. */
export function cellPos(i: number, cols = COLS): string {
  if (!(i >= 0)) return '';
  return colRowPos(i % cols, Math.floor(i / cols));
}

/** The position of the cell under a world point, or '' off the grid. */
export function worldPos(x: number, y: number): string {
  return cellPos(cellAt(x, y));
}
